import { WorkspaceMark } from "src/features/workspaceMark/WorkspaceMark";
import "../styles/globals.css";
import "../lib/initDayjs";

import { ApolloProvider } from "@apollo/client/react";
import { Toaster } from "@repo/ui4/components/Toaster";
import { useWorkspaceIdFromRouter } from "@repo/workspace-route";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { AuthorizedGuard } from "src/components/AuthorizedGuard";
import { LoadingBox } from "src/components/LoadingBox";
import { NotFoundScreen } from "src/components/NotFoundScreen";
import { ArcApolloProvider } from "src/features/arc/ArcApolloProvider";
import { ArcTokenProvider } from "src/features/arc/ArcTokenProvider";
import { AuthProvider } from "src/features/auth/AuthProvider";
import { LyraCsChatUnreadProvider } from "src/features/csChat/LyraCsChatUnreadProvider";
import { FeatureFlagsProvider } from "src/features/featureFlags/FeatureFlagsContext";
import { useApollo } from "src/hooks/useApollo";
import { pagesPath } from "src/utils/$path";

const APP_TITLE = "Speria AI 会話分析";

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const isWorkspacePath = router.pathname.startsWith("/w/[workspaceId]");
  const { isReady, workspaceId } = useWorkspaceIdFromRouter();
  const routeWorkspaceId = isWorkspacePath ? workspaceId : null;

  return (
    <>
      <Head>
        <title>{APP_TITLE}</title>
        <meta name="robots" content="noindex" />
      </Head>
      {isWorkspacePath && !isReady ? (
        <LoadingBox />
      ) : isWorkspacePath && workspaceId == null ? (
        <NotFoundScreen />
      ) : (
        <AppInternal
          key={routeWorkspaceId}
          Component={Component}
          pageProps={pageProps}
          workspaceId={routeWorkspaceId}
        />
      )}
    </>
  );
};

const publicPaths: string[] = [
  pagesPath.debug.auth.$url().pathname,
  pagesPath.$404.$url().pathname,
];
const legacyPaths: string[] = [
  pagesPath.$url().pathname,
  pagesPath._legacyPath([]).$url().pathname,
];

const AppInternal = ({
  Component,
  pageProps,
  workspaceId,
}: {
  Component: AppProps["Component"];
  pageProps: AppProps["pageProps"];
  workspaceId: string | null;
}) => {
  const router = useRouter();
  const client = useApollo(workspaceId);
  const isPublicPath = publicPaths.includes(router.pathname);
  const isLegacyPath = legacyPaths.includes(router.pathname);

  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <div className="min-h-screen bg-background font-sans text-foreground">
          <Toaster />
          <NuqsAdapter>
            {isPublicPath ? (
              <Component {...pageProps} />
            ) : (
              <AuthorizedGuard workspaceId={workspaceId}>
                {isLegacyPath ? (
                  <Component {...pageProps} />
                ) : (
                  <LyraCsChatUnreadProvider>
                    <ArcTokenProvider workspaceId={workspaceId}>
                      <ArcApolloProvider>
                        <FeatureFlagsProvider>
                          <Component {...pageProps} />
                        </FeatureFlagsProvider>
                      </ArcApolloProvider>
                    </ArcTokenProvider>
                  </LyraCsChatUnreadProvider>
                )}
                <WorkspaceMark appTitle={APP_TITLE} />
              </AuthorizedGuard>
            )}
          </NuqsAdapter>
        </div>
      </AuthProvider>
    </ApolloProvider>
  );
};

export default App;
